"use client";

import { useEffect, useId, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { resolveBg } from "@/lib/bg-color";
import { isValidEmail } from "@/lib/email";
import { gtmEvent } from "@/lib/gtm";
import { regionFromPathname, regionPath } from "@/lib/region";
import { cn } from "@/lib/utils";
import { Select } from "@/components/ui/select";
import { UnsavedChangesPrompt } from "@/components/ui/unsaved-changes-prompt";

const COUNTRIES = [
  { label: "Ireland", code: "+353" },
  { label: "United Kingdom", code: "+44" },
];

const inputClass =
  "h-12 w-full rounded-xl border border-black/10 bg-white px-4 text-body text-black placeholder:text-black/30 outline-none transition-colors focus:border-purple-03 focus:ring-1 focus:ring-purple-03";
const labelClass = "mb-1.5 block text-caption font-semibold text-black/60";

type ContactFormBlok = SbBlokData & {
  title?: string;
  intro?: string;
  submit_label?: string;
  consent_label?: string;
  success_heading?: string;
  success_message?: string;
  image?: { filename?: string; alt?: string };
  image_side?: "left" | "right";
  bg_color?: string;
};

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  phone: string;
  message: string;
  consent: boolean;
};

export default function ContactForm({ blok }: { blok: ContactFormBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const privacyHref = regionPath("/privacy-policy", region);
  const { bg, onDark } = resolveBg(blok.bg_color, "white");

  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    email: "",
    country: region === "uk" ? "United Kingdom" : "Ireland",
    phone: "",
    message: "",
    consent: false,
  });
  const [touched, setTouched] = useState({
    email: false,
    phone: false,
    message: false,
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [toast, setToast] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(false), 6000);
    return () => clearTimeout(id);
  }, [toast]);

  const update = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (!dirty) setDirty(true);
  };

  const fieldId = useId();
  const countryId = `${fieldId}-country`;
  const selectedCountry = COUNTRIES.find((c) => c.label === form.country);
  const phoneLength = form.country === "United Kingdom" ? 10 : 9;
  const emailValid = isValidEmail(form.email);
  const phoneComplete = form.phone.length === phoneLength;
  const emailError = touched.email && form.email.trim() !== "" && !emailValid;
  const phoneError = touched.phone && form.phone !== "" && !phoneComplete;
  const messageError = touched.message && form.message.trim() === "";

  const canSubmit =
    form.firstName.trim() !== "" &&
    form.lastName.trim() !== "" &&
    emailValid &&
    phoneComplete &&
    form.message.trim() !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || status === "submitting") return;
    setStatus("submitting");
    try {
      const res = await fetch("/api/contact/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      track(ANALYTICS_EVENTS.CONTACT_FORM_SUBMITTED, { region });
      gtmEvent("contact_submitted", { region });
      setForm((prev) => ({
        firstName: "",
        lastName: "",
        email: "",
        country: prev.country,
        phone: "",
        message: "",
        consent: false,
      }));
      setTouched({ email: false, phone: false, message: false });
      setDirty(false);
      setStatus("idle");
      setToast(true);
    } catch {
      setStatus("error");
    }
  };

  const hasImage = Boolean(blok.image?.filename);
  const imageRight = blok.image_side === "right";

  const formColumn = (
    <>
      <div className="flex flex-col gap-4">
        {blok.title && (
          <h2
            className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
              onDark ? "text-white" : "text-purple-05"
            }`}
          >
            {blok.title}
          </h2>
        )}
        {blok.intro && (
          <p className={`text-body leading-[1.56] ${onDark ? "text-white/80" : "text-black/60"}`}>
            {blok.intro.trim()}
          </p>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-7 flex flex-col gap-4 rounded-2xl bg-white p-6 lg:p-8"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label htmlFor={`${fieldId}-firstName`} className={labelClass}>
              First Name
            </label>
            <input
              id={`${fieldId}-firstName`}
              name="firstName"
              type="text"
              required
              placeholder="First name"
              autoComplete="given-name"
              value={form.firstName}
              onChange={(e) => update("firstName", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor={`${fieldId}-lastName`} className={labelClass}>
              Last Name
            </label>
            <input
              id={`${fieldId}-lastName`}
              name="lastName"
              type="text"
              required
              placeholder="Last name"
              autoComplete="family-name"
              value={form.lastName}
              onChange={(e) => update("lastName", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor={`${fieldId}-email`} className={labelClass}>
            Email
          </label>
          <input
            id={`${fieldId}-email`}
            name="email"
            type="email"
            required
            placeholder="you@email.com"
            autoComplete="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            aria-invalid={emailError}
            className={cn(
              inputClass,
              emailError && "border-red-400 focus:border-red-400 focus:ring-red-400",
            )}
          />
          {emailError && (
            <p className="text-caption mt-1 text-red-500">Enter a valid email address</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label htmlFor={countryId} className={labelClass}>
              Country
            </label>
            <Select
              id={countryId}
              value={form.country}
              onChange={(v) => update("country", v)}
              options={COUNTRIES.map((c) => ({
                value: c.label,
                label: c.label,
              }))}
            />
          </div>
          <div>
            <label htmlFor={`${fieldId}-phone`} className={labelClass}>
              Phone
            </label>
            <div className="flex">
              <span className="text-button flex h-12 items-center rounded-l-xl border border-r-0 border-black/10 bg-black/3 px-3 text-black/60">
                {selectedCountry?.code}
              </span>
              <input
                id={`${fieldId}-phone`}
                name="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                required
                placeholder={form.country === "United Kingdom" ? "7123 456789" : "85 123 4567"}
                maxLength={phoneLength}
                value={form.phone}
                onChange={(e) =>
                  update("phone", e.target.value.replace(/\D/g, "").replace(/^0+/, ""))
                }
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                aria-invalid={phoneError}
                className={cn(
                  "text-body focus:border-purple-03 focus:ring-purple-03 h-12 w-full rounded-r-xl border border-black/10 bg-white px-4 text-black transition-colors outline-none placeholder:text-black/30 focus:ring-1",
                  phoneError && "border-red-400 focus:border-red-400 focus:ring-red-400",
                )}
              />
            </div>
            {phoneError && (
              <p className="text-caption mt-1 text-red-500">Enter your full phone number</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor={`${fieldId}-message`} className={labelClass}>
            Message
          </label>
          <textarea
            id={`${fieldId}-message`}
            name="message"
            required
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, message: true }))}
            rows={5}
            placeholder="How can we help?"
            aria-invalid={messageError}
            className={cn(
              "text-body focus:border-purple-03 focus:ring-purple-03 block w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-black transition-colors outline-none placeholder:text-black/30 focus:ring-1",
              messageError && "border-red-400 focus:border-red-400 focus:ring-red-400",
            )}
          />
          {messageError && (
            <p className="text-caption mt-1 text-red-500">Enter a message</p>
          )}
        </div>

        <label className="flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            name="consent"
            checked={form.consent}
            onChange={(e) => update("consent", e.target.checked)}
            className="peer sr-only"
          />
          <span
            className={`peer-focus-visible:ring-purple-03 flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 ${
              form.consent ? "border-purple-03 bg-purple-03" : "border-black/20 bg-white"
            }`}
          >
            {form.consent && (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-3.5"
              >
                <path d="m5 12 5 5L20 7" />
              </svg>
            )}
          </span>
          <span className="text-button text-black/70">
            {(
              blok.consent_label || "I agree to receive communications from Therapie Fertility."
            ).trim()}
          </span>
        </label>

        {status === "error" && (
          <p className="text-caption text-red-500">
            Something went wrong. Please try again, or email us directly.
          </p>
        )}

        <button
          type="submit"
          disabled={!canSubmit || status === "submitting"}
          className="bg-purple-03 text-body flex h-14 w-full items-center justify-center rounded-xl font-bold text-white transition-opacity disabled:opacity-40"
        >
          {status === "submitting" ? "Submitting…" : (blok.submit_label || "Send message").trim()}
        </button>

        <p className="text-center text-[11px] text-black/60">
          By submitting you agree to our{" "}
          <Link
            href={privacyHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline"
          >
            Privacy Policy
            <span className="sr-only">(opens in a new tab)</span>
          </Link>
        </p>
      </form>
    </>
  );

  return (
    <>
      <UnsavedChangesPrompt active={dirty} />
      <section {...storyblokEditable(blok)} className={bg}>
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-12 lg:py-16">
          {hasImage ? (
            <div
              className={`flex flex-col gap-10 lg:flex-row lg:items-stretch lg:gap-16 ${
                imageRight ? "lg:flex-row-reverse" : ""
              }`}
            >
              <div className="w-full lg:w-[45%] lg:shrink-0">
                <div className="relative aspect-4/5 w-full overflow-hidden rounded-[24px] lg:aspect-auto lg:h-full lg:min-h-[520px]">
                  <Image
                    src={blok.image!.filename!}
                    alt={blok.image!.alt || ""}
                    fill
                    sizes="(min-width: 1024px) 45vw, 100vw"
                    className="object-cover"
                  />
                </div>
              </div>
              <div className="w-full lg:flex-1">{formColumn}</div>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-[680px]">{formColumn}</div>
          )}
        </div>
      </section>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed top-24 right-4 z-100 w-[min(92vw,400px)] sm:right-8"
        >
          <div className="pointer-events-auto flex animate-[fade-in_180ms_ease-out] items-start gap-4 rounded-[20px] bg-white p-5 text-black shadow-[0_12px_28px_-12px_rgba(20,10,60,0.18)] ring-1 ring-black/5">
            <span className="bg-purple-03 mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full text-white">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-4"
                aria-hidden
              >
                <path d="m5 12 5 5L20 7" />
              </svg>
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-midnight text-[15px] leading-tight font-bold">
                {(blok.success_heading || "Thanks for getting in touch").trim()}
              </p>
              <p className="text-button mt-1 leading-snug text-black/60">
                {(
                  blok.success_message || "We've received your message and will be in touch soon."
                ).trim()}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setToast(false)}
              aria-label="Dismiss"
              className="-mt-1 -mr-1 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/5 hover:text-black"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                className="size-4"
                aria-hidden
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
